import { useAtomValue } from 'jotai';
import { useInfiniteQuery } from '@tanstack/react-query';

import { fetchOrganizationNews } from '@/api/v4/organization-news.api';
import { fetchSpotlightCompanyNews } from '@/api/v4/spotlight.api';
import { QueryKey } from '@/config/query-client';

import { companyIdState } from '../company.state';
import {
  type CompanyFetchMode,
  useCompanyFetchMode,
} from '../company-fetch-context';

const COMPANY_NEWS_PAGE_SIZE = 15;

const companyNewsFetchers: Record<
  CompanyFetchMode,
  (
    organizationId: string,
    params: { items: number; nextCursorId?: string },
  ) => ReturnType<typeof fetchOrganizationNews>
> = {
  general: fetchOrganizationNews,
  spotlight: fetchSpotlightCompanyNews,
};

export const useCompanyNews = () => {
  const companyId = useAtomValue(companyIdState);
  const fetchMode = useCompanyFetchMode();

  const getCompanyNews = async (nextPageParam?: string) => {
    if (!companyId) {
      return {
        news: [],
      };
    }

    return companyNewsFetchers[fetchMode](companyId, {
      items: COMPANY_NEWS_PAGE_SIZE,
      nextCursorId: nextPageParam,
    });
  };

  const query = useInfiniteQuery({
    queryKey: [QueryKey.CompanyNews, fetchMode, companyId],
    queryFn: ({ pageParam }) => getCompanyNews(pageParam),
    initialPageParam: undefined as string | undefined,
    gcTime: 1000 * 60 * 5,
    enabled: !!companyId,
    getNextPageParam: lastPage => {
      return lastPage.nextCursor;
    },
    staleTime: 1000 * 60 * 5,
  });

  const pages = query.data ? query.data.pages : [];

  const postedOnTimestamps = pages.flatMap(entry =>
    entry.news.map(n => new Date(n.postedOn).getTime()),
  );

  const latestPostedOnDate = postedOnTimestamps.length
    ? new Date(Math.max(...postedOnTimestamps))
    : null;

  return {
    lastEditedAt: latestPostedOnDate ? latestPostedOnDate.toISOString() : null,
    news: pages.flatMap(page => page.news) ?? [],
    pageSize: COMPANY_NEWS_PAGE_SIZE,
    pages,
    ...query,
  };
};
